/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef } from "react";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { PORTFOLIO_PHOTOS } from "../data";

const PICTIME_SCRIPT_SRC = "https://embedding.pic-time.com/pictures/scripts/compiled/artgalleryembed.js";
const ROSE_GALLERY_SRC = "https://client.therosephoto.com/client?headless=true";

export default function Lookbook() {
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (scriptLoaded.current) return;

    const existingScript = document.querySelector(`script[src="${PICTIME_SCRIPT_SRC}"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = PICTIME_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    scriptLoaded.current = true;
  }, []);

  return (
    <section className="bg-[#F5F2ED] text-[#1A1A1A]" id="lookbook-section">
      {/* Editorial Header */}
      <div className="max-w-4xl mx-auto text-center px-6 pt-16 pb-12">
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#c4a482] font-display flex items-center justify-center gap-1.5 mb-3">
          <Sparkles size={11} /> The Collection Lookbook
        </span>
        <h2 className="font-serif text-[42px] sm:text-[56px] font-extralight tracking-tight leading-none text-[#1A1A1A]">
          The Live Lookbook
        </h2>
        <div className="w-16 h-[1px] bg-[#1A1A1A] mx-auto my-6"></div>
        <p className="font-sans font-light text-xs sm:text-sm text-[#7f7d78] max-w-xl mx-auto leading-relaxed uppercase tracking-wider">
          Browse the real Rose gallery archive, curated from our own photographic collections and delivered through the client gallery experience.
        </p>
      </div>

      {/* Curated Portfolio Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PORTFOLIO_PHOTOS.map((photo) => (
            <article
              key={photo.id}
              className="group border border-[#1A1A1A] bg-[#E8E4DF]/45 transition-colors duration-500 hover:bg-[#E8E4DF]/75"
            >
              <div className="relative aspect-[4/5] overflow-hidden border-b border-[#1A1A1A] bg-[#DCD5C9]">
                <div className="grain-overlay absolute inset-0 z-10 opacity-35" />
                <img
                  src={photo.url}
                  alt={photo.title}
                  referrerPolicy="no-referrer"
                  className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                />
                <span className="absolute left-4 top-4 z-20 bg-[#F5F2ED]/95 px-3 py-1 text-[8px] font-semibold uppercase tracking-[0.24em] text-[#1A1A1A]">
                  {photo.category}
                </span>
              </div>

              <div className="space-y-4 p-5">
                <div>
                  <h3 className="font-serif text-[22px] font-light leading-tight text-[#1A1A1A]">
                    {photo.title}
                  </h3>
                  <p className="mt-1 text-[9px] font-light uppercase tracking-[0.22em] text-[#7f7d78]">
                    {photo.location}
                  </p>
                </div>

                <p className="line-clamp-3 text-xs font-light leading-relaxed text-[#5f5b55]">
                  {photo.narrative}
                </p>

                <div className="grid grid-cols-2 gap-3 border-t border-[#1A1A1A]/15 pt-4 text-[8px] uppercase tracking-[0.18em] text-[#7f7d78]">
                  <div>
                    <span className="block text-[#1A1A1A]">Camera</span>
                    <span>{photo.camera}</span>
                  </div>
                  <div>
                    <span className="block text-[#1A1A1A]">Lens</span>
                    <span>{photo.focalLength}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Live Pic-Time Gallery Embed */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="border border-[#1A1A1A] bg-[#E8E4DF]/50 p-3 sm:p-4">
          <div className="mb-4 flex flex-col gap-3 border-b border-[#1A1A1A]/20 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="block text-[9px] uppercase tracking-[0.28em] text-[#c4a482] font-semibold font-display">
                Client Gallery Archive
              </span>
              <h3 className="font-serif text-[26px] sm:text-[34px] font-light text-[#1A1A1A]">
                Hundreds of Real Rose Images
              </h3>
            </div>
            <a
              href="https://client.therosephoto.com/client"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 self-start border border-[#1A1A1A] px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] transition hover:bg-[#1A1A1A] hover:text-[#F5F2ED] sm:self-auto"
            >
              Open Gallery
              <ArrowUpRight size={12} />
            </a>
          </div>

          <div className="h-[155vh] min-h-[920px] max-h-[1800px] overflow-hidden bg-white">
            <iframe
              frameBorder="0"
              id="pictimeIntegration"
              src={ROSE_GALLERY_SRC}
              title="The Rose Photo Client Gallery"
              className="h-full w-full"
              style={{ width: "100%", height: "100%" }}
              loading="lazy"
              allow="fullscreen"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
